import { Controller } from "@hotwired/stimulus"
import { playPing } from "../utils/ping"

// Bootstrap web-push subscription. Lives once in the dashboard layout.
// - Subscribes silently if permission is already 'granted'.
// - Shows a soft-prompt button when permission is 'default'.
// - Stays out of the way if 'denied'.
export default class extends Controller {
  static targets = ["prompt"]

  connect() {
    // Play an in-app ping when the service worker reports an incoming push
    // while the app is focused (the OS usually stays silent for focused tabs).
    this._onSwMessage = (event) => {
      if (event.data?.type === "play-notification-ping") playPing()
    }
    navigator.serviceWorker?.addEventListener("message", this._onSwMessage)

    if (!this._supported()) return
    this._publicKey = document.querySelector('meta[name="vapid-public-key"]')?.content
    if (!this._publicKey) return

    if (Notification.permission === "granted") {
      this._ensureSubscribed()
    } else if (Notification.permission === "default") {
      this._showPrompt()
    }
  }

  disconnect() {
    if (this._onSwMessage) navigator.serviceWorker?.removeEventListener("message", this._onSwMessage)
  }

  async enable(event) {
    event?.preventDefault()
    if (Notification.permission === "denied") {
      this._hidePrompt()
      return
    }
    const result = await Notification.requestPermission()
    if (result === "granted") {
      await this._ensureSubscribed()
    }
    this._hidePrompt()
  }

  dismiss(event) {
    event?.preventDefault()
    try { sessionStorage.setItem("push_prompt_dismissed", "1") } catch (_) {}
    this._hidePrompt()
  }

  _supported() {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window
  }

  _showPrompt() {
    try { if (sessionStorage.getItem("push_prompt_dismissed")) return } catch (_) {}
    if (this.hasPromptTarget) this.promptTarget.hidden = false
  }

  _hidePrompt() {
    if (this.hasPromptTarget) this.promptTarget.hidden = true
  }

  async _ensureSubscribed() {
    try {
      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this._urlBase64ToUint8Array(this._publicKey)
        })
      }
      await this._postSubscription(sub)
    } catch (err) {
      console.warn("Push subscribe failed:", err)
    }
  }

  async _postSubscription(sub) {
    const json = sub.toJSON()

    // Skip re-POSTing an unchanged subscription. connect() runs on every Turbo
    // navigation, which previously hammered the endpoint many times per minute.
    try {
      if (sessionStorage.getItem("push_endpoint_synced") === json.endpoint) return
    } catch (_) {}

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const res = await fetch("/dashboard/push_subscriptions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrf
      },
      body: JSON.stringify({
        subscription: {
          endpoint:   json.endpoint,
          p256dh_key: json.keys?.p256dh,
          auth_key:   json.keys?.auth
        }
      })
    })

    // Only remember the endpoint once the server accepted it, so a failed POST retries.
    if (res?.ok) {
      try { sessionStorage.setItem("push_endpoint_synced", json.endpoint) } catch (_) {}
    }
  }

  _urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
    const raw = atob(base64)
    const arr = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
    return arr
  }
}
